import React, { useState, useEffect, useRef, useCallback } from "react";
import { AUTH_STORAGE_KEY, SERVER_URL, connectSocket } from "../socket";
import useContacts from "../hooks/useContacts";

const genCode = () => Math.random().toString(36).substring(2, 10).toUpperCase();
const fmt = (d) => new Date(d).toLocaleString("en-IN", { weekday:"short", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });
const getToken = () => { try { return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY))?.token||null; } catch { return null; } };
const getUser  = () => { try { const p=JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY)); return p?.user?.email?.toLowerCase()||p?.user?.username||null; } catch { return null; } };
const hdr = () => { const t=getToken(); return t ? { Authorization:`Bearer ${t}`, "Content-Type":"application/json" } : {"Content-Type":"application/json"}; };
const ICE = { iceServers:[{ urls:"stun:stun.l.google.com:19302" }] };

// API helpers
const api = {
  async list() {
    const r = await fetch(`${SERVER_URL}/api/meetings`, { headers: hdr() });
    if (!r.ok) throw new Error("Failed");
    return (await r.json()).meetings || [];
  },
  async create(payload) {
    const r = await fetch(`${SERVER_URL}/api/meetings`, { method:"POST", headers: hdr(), body: JSON.stringify(payload) });
    if (!r.ok) throw new Error("Failed");
    return (await r.json()).meeting;
  },
  async remove(id) {
    const r = await fetch(`${SERVER_URL}/api/meetings/${id}`, { method:"DELETE", headers: hdr() });
    if (!r.ok) throw new Error("Failed");
  },
  async updateStatus(id, status) {
    const r = await fetch(`${SERVER_URL}/api/meetings/${id}/status`, { method:"PATCH", headers: hdr(), body: JSON.stringify({ status }) });
    if (!r.ok) throw new Error("Failed");
    return (await r.json()).meeting;
  },
  async joinByCode(code) {
    const r = await fetch(`${SERVER_URL}/api/meetings/join/${code}`, { headers: hdr() });
    if (!r.ok) return null;
    return (await r.json()).meeting || null;
  },
};

// ── VIDEO ROOM ──────────────────────────────────────────────────────────────
function VideoRoom({ meeting, onLeave, socket }) {
  const localRef=useRef(null), remoteRefs=useRef({}), pcsRef=useRef({}), streamRef=useRef(null);
  const [peers,setPeers]=useState([]), [micOn,setMicOn]=useState(true), [camOn,setCamOn]=useState(true);
  const [screenOn,setScreenOn]=useState(false), [chatOpen,setChatOpen]=useState(false);
  const [msgs,setMsgs]=useState([]), [msgInput,setMsgInput]=useState(""), [duration,setDuration]=useState(0);
  const me = getUser();

  useEffect(() => {
    (async () => {
      try { const s=await navigator.mediaDevices.getUserMedia({video:true,audio:true}); streamRef.current=s; if(localRef.current) localRef.current.srcObject=s; }
      catch { try { const s=await navigator.mediaDevices.getUserMedia({video:false,audio:true}); streamRef.current=s; } catch {} }
      socket?.emit("meeting:join",{ meetingId:meeting.id, code:meeting.code, user:me });
    })();
    return () => { streamRef.current?.getTracks().forEach(t=>t.stop()); Object.values(pcsRef.current).forEach(pc=>pc.close()); socket?.emit("meeting:leave",{ meetingId:meeting.id, user:me }); };
  }, []);

  useEffect(() => { const t=setInterval(()=>setDuration(d=>d+1),1000); return ()=>clearInterval(t); }, []);
  const fmtDur=s=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  const createPC = useCallback((pid) => {
    const pc = new RTCPeerConnection(ICE);
    pcsRef.current[pid]=pc;
    streamRef.current?.getTracks().forEach(t=>pc.addTrack(t,streamRef.current));
    pc.ontrack=e=>{ const el=remoteRefs.current[pid]; if(el) el.srcObject=e.streams[0]; };
    pc.onicecandidate=e=>{ if(e.candidate) socket?.emit("meeting:ice",{to:pid,candidate:e.candidate,meetingId:meeting.id}); };
    pc.onconnectionstatechange=()=>{ if(["disconnected","failed","closed"].includes(pc.connectionState)){ setPeers(p=>p.filter(x=>x.id!==pid)); delete pcsRef.current[pid]; } };
    return pc;
  }, [socket, meeting]);

  useEffect(() => {
    if (!socket) return;
    const onPeerJoined = async ({peerId,name}) => {
      setPeers(p=>p.find(x=>x.id===peerId)?p:[...p,{id:peerId,name}]);
      const pc=createPC(peerId), offer=await pc.createOffer(); await pc.setLocalDescription(offer);
      socket.emit("meeting:offer",{to:peerId,offer,meetingId:meeting.id});
    };
    const onOffer = async ({from,fromName,offer}) => {
      setPeers(p=>p.find(x=>x.id===from)?p:[...p,{id:from,name:fromName}]);
      const pc=createPC(from); await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer=await pc.createAnswer(); await pc.setLocalDescription(answer);
      socket.emit("meeting:answer",{to:from,answer,meetingId:meeting.id});
    };
    const onAnswer=async({from,answer})=>{ await pcsRef.current[from]?.setRemoteDescription(new RTCSessionDescription(answer)); };
    const onIce=async({from,candidate})=>{ try{ await pcsRef.current[from]?.addIceCandidate(new RTCIceCandidate(candidate)); }catch{} };
    const onLeft=({peerId})=>{ setPeers(p=>p.filter(x=>x.id!==peerId)); pcsRef.current[peerId]?.close(); delete pcsRef.current[peerId]; };
    const onChat=m=>setMsgs(p=>[...p,m]);
    socket.on("meeting:peer-joined",onPeerJoined); socket.on("meeting:offer",onOffer); socket.on("meeting:answer",onAnswer);
    socket.on("meeting:ice",onIce); socket.on("meeting:peer-left",onLeft); socket.on("meeting:chat",onChat);
    return ()=>{ socket.off("meeting:peer-joined",onPeerJoined); socket.off("meeting:offer",onOffer); socket.off("meeting:answer",onAnswer); socket.off("meeting:ice",onIce); socket.off("meeting:peer-left",onLeft); socket.off("meeting:chat",onChat); };
  }, [socket, createPC, meeting]);

  const toggleMic=()=>{ const t=streamRef.current?.getAudioTracks()[0]; if(t){t.enabled=!t.enabled;setMicOn(t.enabled);} };
  const toggleCam=()=>{ const t=streamRef.current?.getVideoTracks()[0]; if(t){t.enabled=!t.enabled;setCamOn(t.enabled);} };
  const toggleScreen=async()=>{
    if(screenOn){
      const cam=await navigator.mediaDevices.getUserMedia({video:true}); const tr=cam.getVideoTracks()[0];
      streamRef.current?.getVideoTracks().forEach(t=>{t.stop();streamRef.current.removeTrack(t);}); streamRef.current?.addTrack(tr);
      Object.values(pcsRef.current).forEach(pc=>pc.getSenders().find(s=>s.track?.kind==="video")?.replaceTrack(tr));
      if(localRef.current) localRef.current.srcObject=streamRef.current; setScreenOn(false);
    } else {
      try { const scr=await navigator.mediaDevices.getDisplayMedia({video:true}); const tr=scr.getVideoTracks()[0];
        Object.values(pcsRef.current).forEach(pc=>pc.getSenders().find(s=>s.track?.kind==="video")?.replaceTrack(tr));
        if(localRef.current) localRef.current.srcObject=scr; tr.onended=()=>setScreenOn(false); setScreenOn(true); } catch{}
    }
  };
  const sendChat=()=>{ if(!msgInput.trim()) return; const m={from:me,text:msgInput.trim(),time:Date.now()}; socket?.emit("meeting:chat-send",{meetingId:meeting.id,...m}); setMsgs(p=>[...p,{...m,own:true}]); setMsgInput(""); };
  const total=peers.length+1;
  const cols=total<=1?"1fr":total<=4?"1fr 1fr":total<=9?"1fr 1fr 1fr":"1fr 1fr 1fr 1fr";

  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"#0d1117",display:"flex",flexDirection:"column"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 20px",flexShrink:0,background:"rgba(255,255,255,0.04)",borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:"#22c55e",animation:"pulse 1.5s infinite"}}/>
          <span style={{color:"#fff",fontWeight:700,fontSize:15}}>{meeting.title}</span>
          <span style={{background:"rgba(255,255,255,0.08)",color:"#94a3b8",fontSize:11,padding:"2px 8px",borderRadius:6,fontFamily:"monospace",letterSpacing:1}}>{meeting.code}</span>
        </div>
        <div style={{display:"flex",gap:16,alignItems:"center"}}>
          <span style={{color:"#64748b",fontSize:13}}>{fmtDur(duration)}</span>
          <span style={{color:"#94a3b8",fontSize:13}}>👥 {total}</span>
        </div>
      </div>
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        <div style={{flex:1,display:"grid",gridTemplateColumns:cols,gap:8,padding:10,overflow:"hidden"}}>
          <Tile label={`${me?.split("@")[0]} (You)`} muted>
            <video ref={localRef} autoPlay muted playsInline style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:10,display:"block"}}/>
            {!camOn && <AvatarOv name={me}/>}
          </Tile>
          {peers.map(p=>(
            <Tile key={p.id} label={p.name?.split("@")[0]||p.id}>
              <video autoPlay playsInline ref={el=>{if(el) remoteRefs.current[p.id]=el;}} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:10,display:"block"}}/>
            </Tile>
          ))}
        </div>
        {chatOpen && (
          <div style={{width:300,flexShrink:0,background:"#161b22",borderLeft:"1px solid rgba(255,255,255,0.07)",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"12px 14px",borderBottom:"1px solid rgba(255,255,255,0.07)",fontWeight:700,color:"#fff",fontSize:14}}>Chat</div>
            <div style={{flex:1,overflowY:"auto",padding:"10px 12px",display:"flex",flexDirection:"column",gap:8}}>
              {msgs.map((m,i)=>(
                <div key={i} style={{alignSelf:m.own?"flex-end":"flex-start",maxWidth:"82%"}}>
                  {!m.own && <div style={{fontSize:10,color:"#64748b",marginBottom:2}}>{m.from?.split("@")[0]}</div>}
                  <div style={{background:m.own?"#1d4ed8":"#1e293b",color:"#fff",fontSize:13,borderRadius:m.own?"12px 12px 2px 12px":"12px 12px 12px 2px",padding:"8px 10px"}}>{m.text}</div>
                  <div style={{fontSize:10,color:"#475569",marginTop:2,textAlign:m.own?"right":"left"}}>{new Date(m.time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:6,padding:10,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
              <input value={msgInput} onChange={e=>setMsgInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendChat()} placeholder="Message…" style={{flex:1,background:"#0d1117",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,padding:"8px 10px",color:"#fff",fontSize:13,outline:"none"}}/>
              <button onClick={sendChat} style={{background:"#1d4ed8",border:"none",borderRadius:8,color:"#fff",padding:"0 12px",cursor:"pointer",fontSize:16}}>➤</button>
            </div>
          </div>
        )}
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"14px 20px",flexShrink:0,background:"rgba(255,255,255,0.04)",borderTop:"1px solid rgba(255,255,255,0.07)"}}>
        <RBtn icon={micOn?"🎤":"🔇"} label={micOn?"Mute":"Unmute"} active={micOn} onClick={toggleMic}/>
        <RBtn icon={camOn?"📷":"📵"} label={camOn?"Stop Video":"Start Video"} active={camOn} onClick={toggleCam}/>
        <RBtn icon="🖥️" label={screenOn?"Stop Share":"Share"} active={!screenOn} onClick={toggleScreen}/>
        <RBtn icon="💬" label="Chat" active={!chatOpen} onClick={()=>setChatOpen(p=>!p)}/>
        <RBtn icon="📵" label="Leave" onClick={onLeave} style={{background:"#ef4444",border:"1px solid #dc2626"}}/>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(1.3)}}`}</style>
    </div>
  );
}
function Tile({label,muted,children}){return(<div style={{position:"relative",background:"#1a1f2e",borderRadius:12,overflow:"hidden",minHeight:120,border:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",justifyContent:"center"}}>{children}<div style={{position:"absolute",bottom:8,left:8,background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:11,padding:"2px 8px",borderRadius:6}}>{label}{muted&&" 🔇"}</div></div>);}
function AvatarOv({name}){return(<div style={{position:"absolute",inset:0,background:"#1a1f2e",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}><div style={{width:64,height:64,borderRadius:"50%",background:"linear-gradient(135deg,#f57011,#ef4444)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:700,color:"#fff"}}>{(name||"?")[0].toUpperCase()}</div><span style={{color:"#64748b",fontSize:12}}>Camera off</span></div>);}
function RBtn({onClick,icon,label,active=true,style={}}){return(<button onClick={onClick} title={label} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,background:active?"rgba(255,255,255,0.08)":"rgba(239,68,68,0.2)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,padding:"8px 14px",cursor:"pointer",color:"#fff",fontSize:20,minWidth:60,transition:"all 0.15s",...style}}>{icon}<span style={{fontSize:10,color:"#94a3b8"}}>{label}</span></button>);}

// ── MAIN PAGE ───────────────────────────────────────────────────────────────
export default function MeetingPage() {
  const { contacts } = useContacts();
  const socketRef = useRef(null);
  const me = getUser();

  const [view, setView]           = useState("home");
  const [meetings, setMeetings]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [active, setActive]       = useState(null);
  const [invites, setInvites]     = useState([]);
  const [showSched, setShowSched] = useState(false);
  const [showJoin, setShowJoin]   = useState(false);
  const [joinCode, setJoinCode]   = useState("");
  const [joinLoad, setJoinLoad]   = useState(false);
  const [joinErr, setJoinErr]     = useState("");
  const [saving, setSaving]       = useState(false);
  const [guestIn, setGuestIn]     = useState("");
  const [form, setForm] = useState({ title:"", date:"", time:"", duration:"60", guests:[], note:"" });

  // Socket init
  useEffect(() => {
    socketRef.current = connectSocket();
    const s = socketRef.current;
    if (!s) return;
    s.emit("meeting:register", { user: me });
    s.on("meeting:invite",    inv  => setInvites(p => p.find(i=>i.id===inv.id)?p:[...p,inv]));
    s.on("meeting:cancelled", ({id}) => { setMeetings(p=>p.filter(m=>m.id!==id)); setInvites(p=>p.filter(i=>i.id!==id)); });
    return () => { s.off("meeting:invite"); s.off("meeting:cancelled"); };
  }, [me]);

  // Load from API
  const load = useCallback(async () => {
    setLoading(true);
    try { setMeetings(await api.list()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Schedule
  const schedule = async () => {
    if (!form.title||!form.date||!form.time) return;
    setSaving(true);
    try {
      const m = await api.create({ title:form.title, startAt:new Date(`${form.date}T${form.time}`).getTime(), duration:Number(form.duration), guests:form.guests, note:form.note });
      setMeetings(p=>[m,...p]);
      form.guests.forEach(g => socketRef.current?.emit("meeting:invite-send",{to:g,meeting:m}));
      setShowSched(false);
      setForm({ title:"", date:"", time:"", duration:"60", guests:[], note:"" });
    } catch(e) { alert("Failed: "+e.message); }
    finally { setSaving(false); }
  };

  // Instant
  const startInstant = async () => {
    setSaving(true);
    try {
      const m  = await api.create({ title:"Instant Meeting", startAt:Date.now(), duration:60, guests:[] });
      const lv = await api.updateStatus(m.id,"live");
      setMeetings(p=>[lv,...p]); setActive(lv); setView("room");
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  // Join by code
  const joinByCode = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoinLoad(true); setJoinErr("");
    try {
      const found = await api.joinByCode(code);
      setActive(found || { id:code, code, title:`Meeting ${code}`, host:"unknown", startAt:Date.now() });
      setView("room"); setShowJoin(false); setJoinCode("");
    } catch { setJoinErr("Meeting not found. Check the code."); }
    finally { setJoinLoad(false); }
  };

  // Start scheduled
  const startNow = async (m) => {
    try {
      const lv = await api.updateStatus(m.id,"live");
      setMeetings(p=>p.map(x=>x.id===m.id?lv:x));
      setActive(lv); setView("room");
      socketRef.current?.emit("meeting:start",{meetingId:m.id,host:me});
    } catch(e) { alert(e.message); }
  };

  const deleteMeeting = async (id) => {
    try { await api.remove(id); setMeetings(p=>p.filter(m=>m.id!==id)); socketRef.current?.emit("meeting:cancel",{meetingId:id}); }
    catch(e) { alert(e.message); }
  };

  const addGuest = () => { const v=guestIn.trim().toLowerCase(); if(!v||form.guests.includes(v)) return; setForm(p=>({...p,guests:[...p.guests,v]})); setGuestIn(""); };

  // Room view
  if (view==="room" && active) {
    return (
      <VideoRoom meeting={active} socket={socketRef.current} onLeave={async()=>{
        if (active.host===me && active.id?.length===36) { try { await api.updateStatus(active.id,"ended"); }catch{} }
        setView("home"); setActive(null); load();
      }}/>
    );
  }

  const upcoming = meetings.filter(m=>m.status==="scheduled"&&m.startAt>Date.now()).sort((a,b)=>a.startAt-b.startAt);
  const recent   = meetings.filter(m=>m.status!=="scheduled"||m.startAt<=Date.now()).sort((a,b)=>b.startAt-a.startAt);
  const canStart = m => m.host===me && Date.now()>=m.startAt-10*60*1000;

  const inp = { width:"100%",boxSizing:"border-box",background:"#1e293b",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"10px 12px",color:"#f1f5f9",fontSize:14,outline:"none" };
  const btn = c => ({ background:c,border:"none",borderRadius:10,padding:"8px 18px",color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer" });

  return (
    <div style={{height:"calc(100dvh - 60px)",background:"var(--bg-primary,#050505)",color:"var(--text-primary,#f1f5f9)",display:"flex",flexDirection:"column",overflowX:"hidden"}}>

      {/* Invite banners */}
      {invites.map(inv=>(
        <div key={inv.id} style={{margin:"10px 16px 0",background:"linear-gradient(90deg,rgba(245,112,17,0.18),rgba(37,99,235,0.18))",border:"1px solid rgba(245,112,17,0.4)",borderRadius:12,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontWeight:700,fontSize:14}}>📅 {inv.title}</div>
            <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>Invited by {inv.host?.split("@")[0]} · {fmt(inv.startAt)}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{setInvites(p=>p.filter(i=>i.id!==inv.id));setActive(inv);setView("room");}} style={btn("#22c55e")}>✓ Accept &amp; Join</button>
            <button onClick={()=>setInvites(p=>p.filter(i=>i.id!==inv.id))} style={btn("#ef4444")}>✕ Decline</button>
          </div>
        </div>
      ))}

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10,padding:"16px 20px 0"}}>
        <div>
          <h2 style={{margin:0,fontSize:22,fontWeight:800}}>📹 Meetings</h2>
          <p style={{margin:"3px 0 0",fontSize:13,color:"#64748b"}}>Video meetings in Kriva</p>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>setShowJoin(true)} style={btn("#1e40af")}>🔗 Join</button>
          <button onClick={()=>setShowSched(true)} style={btn("#f57011")}>＋ Schedule</button>
        </div>
      </div>

      {/* Quick cards */}
      <div style={{display:"flex",gap:10,padding:"16px 20px",overflowX:"auto",scrollbarWidth:"none",flexShrink:0}}>
        {[
          {icon:"🚀",label:"New meeting",sub:"Start instantly",color:"#f57011",action:startInstant},
          {icon:"🔗",label:"Join by code",sub:"Enter meeting code",color:"#2563eb",action:()=>setShowJoin(true)},
          {icon:"📅",label:"Schedule later",sub:"Plan for later",color:"#7c3aed",action:()=>setShowSched(true)},
        ].map(({icon,label,sub,color,action})=>(
          <button key={label} onClick={action} disabled={saving} style={{flex:"0 0 auto",width:115,padding:"10px 7px",background:"var(--bg-secondary,#111827)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:16,cursor:saving?"not-allowed":"pointer",textAlign:"left",color:"inherit",transition:"border-color 0.15s",opacity:saving?0.6:1}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=color} onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"}>
            <div style={{fontSize:30,marginBottom:10}}>{icon}</div>
            <div style={{fontWeight:700,fontSize:13}}>{label}</div>
            <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{sub}</div>
          </button>
        ))}
      </div>

      {/* List */}
      <div style={{flex:1,overflowY:"auto",padding:"0 16px 80px"}}>
        {loading && <div style={{textAlign:"center",padding:30,color:"#64748b"}}><i className="fa-solid fa-circle-notch fa-spin" style={{fontSize:24}}/><p style={{marginTop:10,fontSize:13}}>Loading…</p></div>}

        {!loading && upcoming.length>0 && (<><SL>Upcoming</SL>{upcoming.map(m=><MC key={m.id} m={m} isHost={m.host===me} canStart={canStart(m)} onStart={()=>startNow(m)} onJoin={m.status==="live"?()=>{setActive(m);setView("room");}:null} onDelete={()=>{if(window.confirm("Delete?"))deleteMeeting(m.id);}} onCopy={()=>navigator.clipboard.writeText(m.code)}/>)}</>)}
        {!loading && recent.length>0   && (<><SL>Recent</SL>{recent.slice(0,10).map(m=><MC key={m.id} m={m} isHost={m.host===me} canStart={false} onDelete={()=>{if(window.confirm("Delete?"))deleteMeeting(m.id);}} onCopy={()=>navigator.clipboard.writeText(m.code)}/>)}</>)}
        {!loading && meetings.length===0 && (<div style={{textAlign:"center",padding:"60px 20px",color:"#475569"}}><div style={{fontSize:52,marginBottom:14}}>📹</div><div style={{fontWeight:700,fontSize:16}}>No meetings yet</div><div style={{fontSize:13,marginTop:6}}>Start instantly or schedule one</div></div>)}
      </div>

      {/* SCHEDULE MODAL */}
      {showSched && (
        <Ov onClose={()=>setShowSched(false)} title="📅 Schedule Meeting">
          <Lbl>Title</Lbl>
          <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Weekly standup…" style={inp}/>
          <div style={{display:"flex",gap:10,marginTop:14}}>
            <div style={{flex:1}}><Lbl>Date</Lbl><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inp} min={new Date().toISOString().split("T")[0]}/></div>
            <div style={{flex:1}}><Lbl>Time</Lbl><input type="time" value={form.time} onChange={e=>setForm(p=>({...p,time:e.target.value}))} style={inp}/></div>
          </div>
          <div style={{marginTop:14}}><Lbl>Duration</Lbl>
            <select value={form.duration} onChange={e=>setForm(p=>({...p,duration:e.target.value}))} style={inp}>
              {[15,30,45,60,90,120].map(d=><option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div style={{marginTop:14}}><Lbl>Invite guests</Lbl>
            <div style={{display:"flex",gap:8}}><input value={guestIn} onChange={e=>setGuestIn(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addGuest()} placeholder="Email…" style={{...inp,flex:1}}/><button onClick={addGuest} style={btn("#f57011")}>Add</button></div>
            {guestIn.length>1 && (
              <div style={{background:"#1e293b",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,marginTop:4,maxHeight:130,overflowY:"auto"}}>
                {contacts.filter(c=>(c.name||"").toLowerCase().includes(guestIn.toLowerCase())||(c.emails?.[0]||"").toLowerCase().includes(guestIn.toLowerCase())).slice(0,5).map(c=>(
                  <div key={c.id} onClick={()=>{setForm(p=>({...p,guests:[...p.guests,c.emails[0]]}));setGuestIn("");}} style={{padding:"8px 12px",cursor:"pointer",fontSize:13,color:"#f1f5f9"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><strong>{c.name}</strong> <span style={{color:"#64748b"}}>{c.emails?.[0]}</span></div>
                ))}
              </div>
            )}
            {form.guests.length>0 && (
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                {form.guests.map(g=>(
                  <span key={g} style={{background:"rgba(245,112,17,0.15)",border:"1px solid rgba(245,112,17,0.3)",borderRadius:20,padding:"2px 10px",fontSize:12,color:"#f57011",display:"flex",alignItems:"center",gap:6}}>
                    {g}<button onClick={()=>setForm(p=>({...p,guests:p.guests.filter(x=>x!==g)}))} style={{background:"none",border:"none",color:"#f57011",cursor:"pointer",padding:0,fontSize:14}}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{marginTop:14}}><Lbl>Notes</Lbl><textarea value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))} placeholder="Agenda…" style={{...inp,minHeight:60,resize:"vertical"}}/></div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
            <button onClick={()=>setShowSched(false)} style={btn("#334155")}>Cancel</button>
            <button onClick={schedule} disabled={!form.title||!form.date||!form.time||saving} style={{...btn("#f57011"),opacity:(!form.title||!form.date||!form.time||saving)?0.5:1}}>{saving?"Saving…":"📅 Schedule"}</button>
          </div>
        </Ov>
      )}

      {/* JOIN MODAL */}
      {showJoin && (
        <Ov onClose={()=>{setShowJoin(false);setJoinErr("");}} title="🔗 Join a Meeting">
          <Lbl>Meeting Code</Lbl>
          <input value={joinCode} onChange={e=>{setJoinCode(e.target.value.toUpperCase());setJoinErr("");}} onKeyDown={e=>e.key==="Enter"&&joinCode.length>=4&&joinByCode()} placeholder="e.g. AB12CD34" style={{...inp,fontFamily:"monospace",fontSize:20,letterSpacing:3,textAlign:"center"}} maxLength={12} autoFocus/>
          {joinErr && <div style={{color:"#ef4444",fontSize:12,textAlign:"center",marginTop:8}}>{joinErr}</div>}
          <p style={{fontSize:12,color:"#64748b",textAlign:"center",margin:"10px 0"}}>Get the code from the host</p>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>{setShowJoin(false);setJoinErr("");}} style={btn("#334155")}>Cancel</button>
            <button onClick={joinByCode} disabled={joinCode.length<4||joinLoad} style={{...btn("#2563eb"),opacity:(joinCode.length<4||joinLoad)?0.5:1}}>{joinLoad?"Finding…":"🚀 Join Now"}</button>
          </div>
        </Ov>
      )}
    </div>
  );
}

function MC({m,isHost,canStart,onStart,onJoin,onDelete,onCopy}){
  const [open,setOpen]=useState(false);
  const live=m.status==="live";
  return(
    <div style={{background:"var(--bg-secondary,#111827)",border:live?"1px solid rgba(34,197,94,0.4)":"1px solid rgba(255,255,255,0.07)",borderRadius:14,marginBottom:10,overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",cursor:"pointer"}} onClick={()=>setOpen(p=>!p)}>
        <div style={{width:44,height:44,borderRadius:12,flexShrink:0,background:live?"rgba(34,197,94,0.15)":"rgba(37,99,235,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{live?"🟢":"📅"}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:14,display:"flex",alignItems:"center",gap:8}}>
            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.title}</span>
            {live&&<span style={{fontSize:10,background:"#22c55e",color:"#fff",padding:"1px 6px",borderRadius:10,flexShrink:0}}>LIVE</span>}
          </div>
          <div style={{fontSize:12,color:"#64748b",marginTop:2}}>{fmt(m.startAt)}{m.duration?` · ${m.duration} min`:""}{m.guests?.length?` · ${m.guests.length} invited`:""}</div>
        </div>
        <div style={{display:"flex",gap:6,flexShrink:0}} onClick={e=>e.stopPropagation()}>
          {live&&onJoin&&<button onClick={onJoin} style={{background:"#22c55e",border:"none",borderRadius:8,padding:"5px 12px",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Join</button>}
          {canStart&&onStart&&<button onClick={onStart} style={{background:"#f57011",border:"none",borderRadius:8,padding:"5px 12px",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Start</button>}
          <button onClick={onCopy} style={{background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"5px 8px",color:"#94a3b8",cursor:"pointer"}} title="Copy code">📋</button>
          {isHost&&onDelete&&<button onClick={onDelete} style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"5px 8px",color:"#ef4444",cursor:"pointer"}}>🗑</button>}
        </div>
        <span style={{color:"#475569",fontSize:11,flexShrink:0}}>{open?"▲":"▼"}</span>
      </div>
      {open&&(
        <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",padding:"12px 14px",fontSize:13}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
            <Pl>🔑 {m.code}</Pl><Pl>👤 {m.host?.split("@")[0]}</Pl>{m.duration&&<Pl>⏱ {m.duration} min</Pl>}
          </div>
          {m.guests?.length>0&&(<div style={{marginBottom:8}}><div style={{fontSize:11,color:"#475569",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Guests</div><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{m.guests.map(g=><Pl key={g}>{g}</Pl>)}</div></div>)}
          {m.note&&<div style={{color:"#64748b",fontSize:12,marginTop:4}}>{m.note}</div>}
        </div>
      )}
    </div>
  );
}
function Ov({title,onClose,children}){return(<div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}><div style={{background:"#0f172a",border:"1px solid rgba(255,255,255,0.1)",borderRadius:18,padding:"24px 22px",width:"100%",maxWidth:460,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 64px rgba(0,0,0,0.8)"}} onClick={e=>e.stopPropagation()}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}><h3 style={{margin:0,fontSize:17,fontWeight:800,color:"#f1f5f9"}}>{title}</h3><button onClick={onClose} style={{background:"rgba(255,255,255,0.08)",border:"none",color:"#94a3b8",width:30,height:30,borderRadius:"50%",cursor:"pointer",fontSize:16}}>×</button></div>{children}</div></div>);}
function Lbl({children}){return(<div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{children}</div>);}
function SL({children}){return(<div style={{fontSize:11,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:1,padding:"8px 0 6px"}}>{children}</div>);}
function Pl({children}){return(<span style={{background:"rgba(255,255,255,0.06)",borderRadius:8,padding:"3px 10px",fontSize:12,color:"#94a3b8"}}>{children}</span>);}
